import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject,
         OnDestroy
       } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { GalleryService } from 'app/gallery/gallery.service';
import { ProductConfig } from 'app/product-designer-config';
import { StageService } from 'collage-designer/stage/stage.service';
import { ProductService } from 'core/product/product.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber
       } from 'core/utils/async-event-emitter';
import { Subscription } from 'rxjs';
import { PartConfig, TemplateConfig } from '../collage-designer-config';
import { DesignStepService } from '../design-step/design-step.service';

@Component({
    selector: 'advent-calendar-overlay',
    templateUrl: './advent-calendar-overlay.component.html',
    styleUrls: ['./advent-calendar-overlay.component.scss'],
    standalone: false
})
export class AdventCalendarOverlayComponent
    implements AfterViewInit, OnDestroy
{
  private static readonly CATALOG_PRODUCT_ID: RegExp = /advent-calendar/;
  private static readonly PART_ID: string = 'motive';
  private static readonly OVERLAY_IMAGE_LANDSCAPE: string = Utils.url(
      './assets/img/advent-calendar-overlay/raster-advent-landscape.svg');
  private static readonly OVERLAY_IMAGE_PORTRAIT: string = Utils.url(
      './assets/img/advent-calendar-overlay/raster-advent-portrait.svg');

  public isApplicable: boolean = false;
  public isOverlayVisible: boolean = false;
  private isProductApplicable: boolean = false;
  private isInitialOverlayPending: boolean = false;
  private partId: string|undefined;

  private templateChangedSubscription:
      AsyncEventEmitterSubscriber<TemplateConfig>;
  private uploadProgressChangedSubscription:
      AsyncEventEmitterSubscriber<boolean>;
  private productHasChangedSubscription:
      AsyncEventEmitterSubscriber<ProductConfig>;
  private partChangedSubscription: AsyncEventEmitterSubscriber<PartConfig>;
  private stageConfigAppliedSubscription:
      AsyncEventEmitterSubscriber<boolean>;
  private createNewPlaceholderInitializedSubscription: Subscription;

  constructor (
      private cdr: ChangeDetectorRef,
      private elementRef: ElementRef,
      private mir: MatIconRegistry,
      private sanitizer: DomSanitizer,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(StageService) private stageService: StageService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(GalleryService) private galleryService: GalleryService)
  {
    this.mir.addSvgIcon(
        'advent-calendar-overlay',
        this.sanitizer.bypassSecurityTrustResourceUrl(Utils.url(
            './assets/img/icons/calendar.svg')));

    this.adoptProduct(this.productService.getCurrentProduct());
    this.adoptPart(this.designStepService.getParts().length > 0
        ? this.designStepService.getPart()
        : undefined);

    this.productHasChangedSubscription =
        this.productService.productHasChanged.subscribe(
            async (productConfig: ProductConfig) => {
              this.removeOverlay();
              this.adoptProduct(productConfig);
              this.cdr.detectChanges();
            });

    this.partChangedSubscription =
        this.designStepService.partChanged.subscribe(
            async (partConfig: PartConfig) => {
              this.removeOverlay();
              this.adoptPart(partConfig);
              this.cdr.detectChanges();
            });

    this.stageConfigAppliedSubscription =
        this.stageService.stageConfigApplied.subscribe(
            async () => this.insertInitialOverlay());

    this.templateChangedSubscription =
        this.designStepService.templateChanged.subscribe(
            async () => this.removeOverlay());

    this.uploadProgressChangedSubscription =
        this.galleryService.uploadProgressChanged.subscribe(
            async (uploadInProgress: boolean) => {
              if (uploadInProgress) {
                this.removeOverlay();
              }
            });

    this.createNewPlaceholderInitializedSubscription =
        this.designStepService.createNewPlaceholderInitialized.subscribe(
            () => this.removeOverlay());
  }

  public ngAfterViewInit(): void
  {
    if (this.stageService.getIsStageLoaded()) {
      this.insertInitialOverlay();
    }
  }

  public ngOnDestroy(): void
  {
    this.removeOverlay();
    this.productService.productHasChanged.unsubscribe(
        this.productHasChangedSubscription);
    this.designStepService.partChanged.unsubscribe(
        this.partChangedSubscription);
    this.stageService.stageConfigApplied.unsubscribe(
        this.stageConfigAppliedSubscription);
    this.designStepService.templateChanged.unsubscribe(
        this.templateChangedSubscription);
    this.galleryService.uploadProgressChanged.unsubscribe(
        this.uploadProgressChangedSubscription);
    this.createNewPlaceholderInitializedSubscription.unsubscribe();
  }

  public onTriggerClicked(): void
  {
    if (this.isOverlayVisible) {
      this.removeOverlay();
    }
    else {
      this.insertOverlay();
    }
  }

  private adoptProduct(productConfig: ProductConfig|undefined): void
  {
    this.isProductApplicable = !!productConfig &&
        AdventCalendarOverlayComponent.CATALOG_PRODUCT_ID.test(
            productConfig.id);
    this.isInitialOverlayPending = this.isProductApplicable;
    this.updateIsApplicable();
  }

  private adoptPart(partConfig: PartConfig|undefined): void
  {
    this.partId = partConfig?.id;
    this.updateIsApplicable();
  }

  private updateIsApplicable(): void
  {
    this.isApplicable = this.isProductApplicable &&
        this.partId === AdventCalendarOverlayComponent.PART_ID;
  }

  private insertInitialOverlay(): void
  {
    if (!this.isInitialOverlayPending || !this.isApplicable) {
      return;
    }
    this.isInitialOverlayPending = false;
    this.insertOverlay();
    this.cdr.detectChanges();
  }

  private insertOverlay(): void
  {
    if (this.isOverlayVisible || !this.isApplicable) {
      return;
    }
    const stage = this.getRootNode().querySelector('.stage-svg-container');
    if (!stage) {
      return;
    }
    const stageConfig = this.stageService.getCurrentStageState().stageConfig;

    const img = document.createElement('img');
    img.classList.add('advent-calendar-overlay');
    img.src = stageConfig.rotation === 0
        ? AdventCalendarOverlayComponent.OVERLAY_IMAGE_LANDSCAPE
        : AdventCalendarOverlayComponent.OVERLAY_IMAGE_PORTRAIT;
    stage.insertBefore(img, stage.firstChild);
    this.isOverlayVisible = true;
  }

  private removeOverlay(): void
  {
    const img = this.getRootNode().querySelector('.advent-calendar-overlay');
    if (img) {
      img.remove();
    }
    this.isOverlayVisible = false;
  }

  private getRootNode(): Document|ShadowRoot
  {
    return <Document|ShadowRoot>this.elementRef.nativeElement.getRootNode();
  }
}
